import React, { useEffect, useRef, useState } from 'react';
import { analyzeWithGemini, formatAnalysisError } from '../utils/geminiApi';
import './VideoInterview.css';

interface VideoInterviewProps {
  stream: MediaStream | null;
  onStartWatching: () => void;
  onBack: () => void;
}

interface PreparedAnswer {
  questions: string[];
  answers: string[];
  fullScript: string;
  wordCount: number;
  estimatedSeconds: number;
}

const PRACTICE_SECONDS = 180;

function breakAfterTranslation(text: string): string {
  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return lines
    .map((line) => (/[가-힣]/.test(line) ? `${line}\n` : line))
    .join('\n')
    .trim();
}

function countWords(text: string): number {
  const english = text
    .split('\n')
    .filter((line) => line.trim() && !/[가-힣]/.test(line))
    .join(' ');
  return english.trim().split(/\s+/).filter(Boolean).length;
}

function formatClock(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function readFrame(video: HTMLVideoElement): { data: string; hash: string } | null {
  if (!video.videoWidth || !video.videoHeight) return null;

  const maxWidth = 960;
  const scale = Math.min(1, maxWidth / video.videoWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  const thumb = document.createElement('canvas');
  thumb.width = 16;
  thumb.height = 16;
  const thumbContext = thumb.getContext('2d', { willReadFrequently: true });
  if (!thumbContext) return null;
  thumbContext.drawImage(canvas, 0, 0, 16, 16);
  const pixels = thumbContext.getImageData(0, 0, 16, 16).data;
  let hash = '';
  for (let index = 0; index < pixels.length; index += 16) {
    hash += String.fromCharCode(pixels[index]);
  }

  const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
  return { data: dataUrl.split(',')[1] || '', hash };
}

function normalizeAnswer(analysis: any): PreparedAnswer {
  const questions = Array.isArray(analysis?.questions)
    ? analysis.questions.map((item: unknown) => String(item).trim()).filter(Boolean)
    : [];
  const answers = Array.isArray(analysis?.answers)
    ? analysis.answers.map((item: unknown) => String(item).trim()).filter(Boolean)
    : [];
  const fullScript = breakAfterTranslation(
    String(analysis?.fullScript || answers.join('\n\n') || analysis?.text || '')
  );
  const formattedAnswers = answers.map(breakAfterTranslation);
  if (!questions.length || !fullScript) {
    throw new Error('화면에 질문이 아직 보이지 않습니다.');
  }
  const wordCount = countWords(fullScript);
  return {
    questions,
    answers: formattedAnswers,
    fullScript,
    wordCount,
    estimatedSeconds: Math.round((wordCount / 140) * 60)
  };
}

const VideoInterview: React.FC<VideoInterviewProps> = ({ stream, onStartWatching, onBack }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [prepared, setPrepared] = useState<PreparedAnswer | null>(null);
  const [statusText, setStatusText] = useState('브라우저가 화면을 보고 있습니다.');
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(PRACTICE_SECONDS);
  const [timerRunning, setTimerRunning] = useState(false);
  const preparedKeyRef = useRef('');

  useEffect(() => {
    if (!timerRunning) return undefined;
    const timer = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          setTimerRunning(false);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [timerRunning]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) {
      setStatusText('브라우저가 볼 화면을 아직 고르지 않았습니다.');
      return undefined;
    }

    video.srcObject = stream;
    video.muted = true;
    video.play().catch(() => undefined);
    setError(null);
    setStatusText('브라우저가 화면을 보고 있습니다.');

    const track = stream.getVideoTracks()[0];
    const handleEnded = () => {
      setStatusText('화면 보기가 멈췄습니다.');
    };
    track?.addEventListener('ended', handleEnded);

    let stopped = false;
    let lastHash = '';
    let stableHash = '';
    let analyzedHash = '';
    let busy = false;

    const timer = window.setInterval(async () => {
      if (stopped || busy || track?.readyState === 'ended') return;
      const frame = readFrame(video);
      if (!frame || !frame.data) return;

      if (frame.hash !== lastHash) {
        lastHash = frame.hash;
        stableHash = '';
        setStatusText('화면이 바뀌었습니다. 질문이 멈추면 읽습니다.');
        return;
      }

      if (stableHash !== frame.hash) {
        stableHash = frame.hash;
        return;
      }

      if (analyzedHash === frame.hash) return;

      busy = true;
      setStatusText('질문을 읽고 있습니다.');
      try {
        const data = await analyzeWithGemini({
          userAnswer: 'interview',
          sampleAnswer: 'interview',
          question: '',
          analysisType: 'interview-answer',
          images: [{ data: frame.data, mimeType: 'image/jpeg' }]
        });
        if (!data.success || !data.analysis) {
          throw new Error(data.error || '질문을 읽지 못했습니다.');
        }
        const next = normalizeAnswer(data.analysis);
        const key = next.questions.join('\n');
        analyzedHash = frame.hash;
        if (key !== preparedKeyRef.current) {
          preparedKeyRef.current = key;
          setPrepared(next);
          setSecondsLeft(PRACTICE_SECONDS);
          setTimerRunning(false);
        }
        setError(null);
        setStatusText('질문을 파악했습니다.');
      } catch (watchError) {
        const message = formatAnalysisError(watchError);
        if (message.includes('질문이 아직')) {
          setStatusText(message);
          setError(null);
        } else {
          setError(message);
          setStatusText('질문을 다시 읽는 중입니다.');
        }
      } finally {
        busy = false;
      }
    }, 1200);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      track?.removeEventListener('ended', handleEnded);
      video.srcObject = null;
    };
  }, [stream]);

  const answerBlocks = prepared && prepared.questions.length > 0 && prepared.answers.length > 0
    ? prepared.questions.map((question, index) => ({
        question,
        answer: prepared.answers[index] || ''
      })).filter((block) => block.answer)
    : [];

  return (
    <div className="video-interview">
      <header className="video-interview-header">
        <button type="button" onClick={onBack} className="video-interview-back">
          ← 뒤로 가기
        </button>
        <h1>영상 면접 3분 답변</h1>
      </header>

      <main className="video-interview-main">
        <p className="video-interview-lead">
          브라우저가 면접 창을 직접 보고, 화면에 나온 질문 3개에 맞춰 약 3분 분량의 답을 띄웁니다.
        </p>
        <p className="video-interview-status">{statusText}</p>
        {!stream && (
          <button type="button" className="video-interview-text-submit" onClick={onStartWatching}>
            브라우저가 화면 보게 하기
          </button>
        )}
        <video ref={videoRef} className="video-interview-live" autoPlay muted playsInline />
        {error && <p className="video-interview-error">{error}</p>}

        {prepared && (
          <section className="video-interview-result">
            <div className="video-interview-meta">
              <span>{prepared.wordCount}단어</span>
              <span>읽는 시간 약 {formatClock(prepared.estimatedSeconds)}</span>
              <button
                type="button"
                onClick={() => {
                  setSecondsLeft(PRACTICE_SECONDS);
                  setTimerRunning(true);
                }}
              >
                {timerRunning ? `녹음 ${formatClock(secondsLeft)}` : secondsLeft === 0 ? '3분 다시 시작' : '3분 타이머 시작'}
              </button>
              {timerRunning && (
                <button type="button" onClick={() => setTimerRunning(false)}>
                  일시정지
                </button>
              )}
            </div>

            {answerBlocks.length > 0 ? (
              answerBlocks.map((block, index) => (
                <article key={`${block.question}-${index}`} className="video-interview-block">
                  <h2>질문 {index + 1}</h2>
                  <p className="video-interview-question">{block.question}</p>
                  <p className="video-interview-answer">{block.answer}</p>
                </article>
              ))
            ) : (
              <article className="video-interview-block">
                <h2>3분 답변</h2>
                <p className="video-interview-answer">{prepared.fullScript}</p>
              </article>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default VideoInterview;
